import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import StarIcon from '@mui/icons-material/Star';
import { makeApiCall } from '../../config/api';
import { formatScheduledDate } from '../../utils/dateUtils';

const EmployeeDisciplineReport = () => {
  const { t } = useTranslation();
  const [reportType, setReportType] = useState('warnings'); // 'warnings' or 'points'
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Fetch employees list
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await makeApiCall('api/time-tracking/admin/employees-list');
        if (response.ok) {
          const result = await response.json();
          setEmployees(result.employees || []);
        }
      } catch (err) {
        console.error('Error fetching employees:', err);
      }
    };
    fetchEmployees();

    // Auto-load last 90 days (discipline events are rarer)
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);
    setFromDate(from);
    setToDate(to);
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate.toISOString());
      if (toDate) params.append('to_date', toDate.toISOString());
      
      if (selectedEmployees.length > 0) {
        const empIds = selectedEmployees.map(e => e.employee_id).join(',');
        params.append('employee_ids', empIds);
      }

      const endpoint = reportType === 'warnings' 
        ? 'api/reports/employee-warnings' 
        : 'api/reports/employee-points';

      const response = await makeApiCall(`${endpoint}?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching discipline report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setGeneratingPDF(true);
    setError(null);
    try {
      const empIds = selectedEmployees.length > 0 
        ? selectedEmployees.map(e => e.employee_id).join(',') 
        : null;

      const endpoint = reportType === 'warnings' 
        ? 'api/reports/employee-warnings/pdf' 
        : 'api/reports/employee-points/pdf';

      const response = await makeApiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate ? fromDate.toISOString() : null,
          to_date: toDate ? toDate.toISOString() : null,
          employee_ids: empIds
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `employee-${reportType}-report-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(t('reports.error.pdf'));
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('reports.error.pdf'));
    } finally {
      setGeneratingPDF(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReport();
    }
  }, [reportType]); // Re-fetch when type changes

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value ? new Date(e.target.value) : null);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value ? new Date(e.target.value) : null);
  };

  const handleReportTypeChange = (event, newType) => {
    if (newType !== null) {
      setReportType(newType);
    }
  };

  const getWarningTypeLabel = (type) => {
    const types = { 1: 'Verbal/First', 2: 'Written/Second', 3: 'Final' };
    return types[type] || 'Unknown';
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <ToggleButtonGroup
              value={reportType}
              exclusive
              onChange={handleReportTypeChange}
              fullWidth
              size="small"
            >
              <ToggleButton value="warnings" color="error">
                <WarningIcon sx={{ mr: 1 }} /> {t('reports.discipline.warnings')}
              </ToggleButton>
              <ToggleButton value="points" color="primary">
                <StarIcon sx={{ mr: 1 }} /> {t('reports.discipline.points')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
              fullWidth
              label={t('reports.fromDate')}
              type="date"
              value={formatDateForInput(fromDate)}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
              fullWidth
              label={t('reports.toDate')}
              type="date"
              value={formatDateForInput(toDate)}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Autocomplete
              multiple
              options={employees}
              getOptionLabel={(option) => option.employee_name || `${option.first_name} ${option.last_name}`}
              value={selectedEmployees}
              onChange={(event, newValue) => setSelectedEmployees(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('reports.employee')} 
                  placeholder={t('reports.selectEmployee')}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={fetchReport}
                disabled={loading || !fromDate || !toDate}
                sx={{ bgcolor: '#1976d2', minWidth: '40px', px: 2 }}
              >
                <RefreshIcon />
              </Button>
              <Button
                variant="contained"
                startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={downloadPDF}
                disabled={generatingPDF || data.length === 0}
                sx={{ bgcolor: '#d32f2f', flexGrow: 1 }}
              >
                PDF
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Report Data */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('reports.noData')}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {/* Summary Cards */}
          <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Stack direction="row" spacing={3}>
                <Typography variant="h6">
                  {reportType === 'warnings' ? t('reports.discipline.warnings') : t('reports.discipline.points')} Summary:
                </Typography>
                <Chip 
                  label={`Total Records: ${data.length}`} 
                  color={reportType === 'warnings' ? 'error' : 'primary'} 
                />
                {reportType === 'points' && (
                  <>
                    <Chip 
                      label={`${t('reports.discipline.awarded')}: ${data.reduce((sum, d) => sum + (d.points_awarded || 0), 0)}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip 
                      label={`${t('reports.discipline.deducted')}: ${data.reduce((sum, d) => sum + (d.points_deducted || 0), 0)}`}
                      color="error"
                      variant="outlined"
                    />
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ bgcolor: reportType === 'warnings' ? '#ffebee' : '#e3f2fd' }}>
                <TableRow>
                  <TableCell><strong>{t('reports.date')}</strong></TableCell>
                  <TableCell><strong>{t('reports.employee')}</strong></TableCell>
                  {reportType === 'warnings' ? (
                    <>
                      <TableCell><strong>{t('reports.discipline.warningType')}</strong></TableCell>
                      <TableCell><strong>{t('reports.discipline.infraction')}</strong></TableCell>
                      <TableCell><strong>{t('reports.discipline.issuedBy')}</strong></TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell><strong>{t('reports.discipline.conduct')}</strong></TableCell>
                      <TableCell align="right"><strong>{t('reports.discipline.awarded')}</strong></TableCell>
                      <TableCell align="right"><strong>{t('reports.discipline.deducted')}</strong></TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatScheduledDate(reportType === 'warnings' ? row.warning_date : row.point_date)}</TableCell>
                    <TableCell>{row.employee_name}</TableCell>
                    {reportType === 'warnings' ? (
                      <>
                        <TableCell>
                          <Chip 
                            label={getWarningTypeLabel(row.type_of_warning)} 
                            color={row.type_of_warning === 3 ? 'error' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{row.description_of_infraction}</TableCell>
                        <TableCell>{row.manager_name || '-'}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{row.description_of_conduct || row.description_of_misconduct || '-'}</TableCell>
                        <TableCell align="right" sx={{ color: 'green', fontWeight: 'bold' }}>
                          {row.points_awarded > 0 ? `+${row.points_awarded}` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'red', fontWeight: 'bold' }}>
                          {row.points_deducted > 0 ? `-${row.points_deducted}` : '-'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default EmployeeDisciplineReport;

